import {
  TableContainer, CardBody, Card, Button, useToast, useDisclosure, Modal, ModalOverlay,
  ModalContent, ModalCloseButton, ModalBody, ModalHeader, ModalFooter, Box, FormControl,
  FormLabel, Input, Select, Grid, Tag, Text,
} from '@chakra-ui/react'
import {useNavigate, useSearchParams} from "react-router-dom";
import {DataTable} from "../components/dataTable";
import {createColumnHelper} from "@tanstack/react-table";
import {useAppDataStore} from "../mobx/appDataStore";
import {useEffect, useMemo, useState} from "react";
import {DeleteIcon} from "@chakra-ui/icons";
import {useFormik} from "formik";

const columnHelper = createColumnHelper();

const MailAccounts = () => {
  const appDataStore = useAppDataStore()
  const {socket} = appDataStore
  const rol = appDataStore.user?.rol?.name?.toLowerCase()
  const isDistribuidor = rol === 'distribuidor'
  const isAdmin = rol === 'admin'
  const [data, setData] = useState([])
  const [distribuidores, setDistribuidores] = useState([])
  const [refreshDependencies, setRefreshDependencies] = useState(0)
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {isOpen: isZohoOpen, onOpen: onZohoOpen, onClose: onZohoClose} = useDisclosure()

  const formik = useFormik({
    initialValues: {email: '', password: '', distribuidorId: ''},
    onSubmit: (values) => {
      socket.emit('mail/accounts/zoho/link', {...values, distribuidorId: values.distribuidorId || null}, (res) => {
        if (res.result === 'error') {
          return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
        }
        onZohoClose()
        formik.resetForm()
        return toast({title: 'Vinculado', description: 'La cuenta Zoho se vinculó con éxito', status: 'success', duration: 9000, isClosable: true})
      })
    },
  })

  useEffect(() => {
    document.title = 'Tienda Digital - Cuentas de correo'
  }, []);

  useEffect(() => {
    const hotmail = searchParams.get('hotmail')
    if (!hotmail) return
    toast({
      title: hotmail === 'success' ? 'Hotmail vinculado' : 'Error al vincular Hotmail',
      description: searchParams.get('message') || undefined,
      status: hotmail === 'success' ? 'success' : 'error',
      duration: 9000, isClosable: true,
    })
    setSearchParams({})
  }, []);

  useEffect(() => {
    if (!isAdmin) return
    socket.emit('datasource', {
      model: 'User', records: 1000, currentPage: 1, searchAttributes: [], include: ['Rol']
    }, (res) => setDistribuidores((res.data || []).filter(u => ['distribuidor', 'subadmin'].includes(u.Rol?.name))))
  }, []);

  useEffect(() => {
    socket.on('mail/accounts/updated', () => setRefreshDependencies(Math.random()))
    return () => socket.off('mail/accounts/updated')
  }, []);

  const connectMicrosoft = () => {
    socket.emit('mail/accounts/microsoft/url', {}, (res) => {
      if (res.result === 'error') {
        return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      }
      window.location.href = res.url
    })
  }

  const toggleAccount = (row) => {
    socket.emit('mail/accounts/toggle', {id: row.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setRefreshDependencies(Math.random())
    })
  }

  const deleteAccount = (row) => {
    socket.emit('mail/accounts/delete', {id: row.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setRefreshDependencies(Math.random())
      return toast({title: 'Eliminado', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const dataFetch = async (records, currentPage, order, search, filter, attributes) => {
    return new Promise(resolve => {
      socket.emit('datasource', {model: 'MailAccount', records, currentPage, order, search, filter, attributes}, resolve)
    })
  };

  const columns = useMemo(() => ([
    columnHelper.accessor('id', {cell: (info) => info.getValue(), header: "ID"}),
    columnHelper.accessor('email', {cell: (info) => info.getValue(), header: "Correo"}),
    columnHelper.accessor('provider', {cell: (info) => info.getValue(), header: "Proveedor"}),
    columnHelper.accessor('distribuidorId', {
      enableColumnFilter: false,
      cell: (info) => {
        const id = info.getValue()
        const distribuidor = distribuidores.find(d => d.id === id)
        return distribuidor ? distribuidor.name : 'Admin'
      }, header: "Dueño"
    }),
    columnHelper.accessor('enabled', {
      cell: (info) => info.getValue()
        ? <Tag colorScheme='green' variant='outline'>Activa</Tag>
        : <Tag colorScheme='red' variant='outline'>Inactiva</Tag>,
      header: "Estado"
    }),
    columnHelper.accessor('createdAt', {cell: (info) => Object.datetime(info.getValue()), header: "Vinculada"}),
    columnHelper.accessor('', {
      cell: (info) => <Button size={'sm'} onClick={() => toggleAccount(info.row.original)}>
        {info.row.original.enabled ? 'Desactivar' : 'Activar'}
      </Button>, header: "Acción"
    }),
    columnHelper.accessor('', {
      cell: (info) => <Button color={'red'} colorScheme='teal' variant={'outline'} size={'sm'}
                              onClick={() => deleteAccount(info.row.original)}><DeleteIcon/></Button>,
      header: "Eliminar"
    }),
  ]), [distribuidores])

  if (isDistribuidor) {
    return <Card marginTop={5} marginLeft={5}><CardBody>
      <Text>No tiene permisos para ver esta sección.</Text>
    </CardBody></Card>
  }

  return (
    <Grid>
      <Card marginTop={5} marginLeft={5}>
        <CardBody>
          <Button colorScheme='blue' onClick={connectMicrosoft} mb={5}>Conectar con Microsoft (Hotmail)</Button>
          <Button marginLeft={2} colorScheme='blue' onClick={onZohoOpen} mb={5}>Vincular cuenta Zoho</Button>
          {isAdmin && (
            <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailConfig')} mb={5}>
              Configuración Microsoft
            </Button>
          )}
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailLinksNetflix')} mb={5}>
            Links Netflix
          </Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailLinks')} mb={5}>
            Links Disney/Crunchyroll
          </Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/chatgpt')} mb={5}>
            Links ChatGPT
          </Button>

          <TableContainer overflowX={'visible'} whiteSpace={'pre-wrap'} maxWidth={'1fr'}>
            <DataTable size={'sm'} columns={columns} data={data} dataFetch={dataFetch}
                       refreshDependencies={refreshDependencies}/>
          </TableContainer>
        </CardBody>

        <Modal isOpen={isZohoOpen} onClose={onZohoClose}>
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Vincular cuenta Zoho</ModalHeader>
            <ModalCloseButton/>
            <ModalBody pb={6}>
              <FormControl>
                <FormLabel>Correo</FormLabel>
                <Input name={'email'} onChange={formik.handleChange} value={formik.values.email}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Contraseña</FormLabel>
                <Input type={'password'} name={'password'} onChange={formik.handleChange} value={formik.values.password}/>
              </FormControl>
              {isAdmin && (
                <FormControl mt={4}>
                  <FormLabel>Asignar a (opcional)</FormLabel>
                  <Select name={'distribuidorId'} placeholder={'Sin asignar (admin)'}
                          onChange={formik.handleChange} value={formik.values.distribuidorId}>
                    {distribuidores.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </FormControl>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme='blue' mr={3} onClick={formik.submitForm}>Vincular</Button>
              <Button onClick={onZohoClose}>Cancelar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Card>
    </Grid>
  )
}

export default MailAccounts
